import Uppy, { UploadResult } from '@uppy/core';
import Russian from '@uppy/locales/lib/ru_RU';
import { DashboardModal } from '@uppy/react';
import Webcam from '@uppy/webcam';
import { forEach } from 'lodash';
import React, { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';

import { payoutOrdersApi } from 'api';
import { QueryKey } from 'enums';
import { useMutation, useUser } from 'hooks';
import { PayoutOrder } from 'types';

// Don't forget the CSS: core and the UI components + plugins you are using.
import '@uppy/core/dist/style.min.css';
import '@uppy/dashboard/dist/style.min.css';
import '@uppy/webcam/dist/style.min.css';

const uppy = new Uppy({
  autoProceed: false,
  allowMultipleUploads: true,
  restrictions: {
    maxNumberOfFiles: 10,
    allowedFileTypes: ['image/*', 'application/pdf'], // Only allow image files and pdf
    maxFileSize: 1024 * 1024 * 3, // 3MB max file size
  },
}).use(Webcam);

type Props = {
  open: boolean;
  order: PayoutOrder;
  onClose: () => void;
};

export const UploadReceiptsDialog: React.FC<Props> = ({
  open,
  order,
  onClose,
}) => {
  const { t: tCommon } = useTranslation();

  const queryClient = useQueryClient();
  const { role } = useUser();

  const removeFiles = useCallback(() => {
    const files = uppy.getFiles();
    forEach(files, (file) => {
      uppy.removeFile(file.id);
    });
  }, []);

  const { mutate: uploadPaymentReceipts } = useMutation(
    payoutOrdersApi.uploadReceiptsAsRole(role),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([QueryKey.PayoutOrderReceipts, order.id]);
        removeFiles();
        onClose();
      },
      notifierType: 'execute',
      notifierMessages: {
        success: (data: {
          total: number;
          uploaded: string[];
          failed: string[];
        }) => {
          if (!data?.total) {
            return;
          }
          if (!data.failed?.length) {
            return tCommon('common.all_files_uploaded');
          }
          return tCommon('common.some_files_uploaded', {
            uploaded: data.uploaded?.length,
            total: data.total,
          });
        },
      },
    },
  );

  const handleFilesComplete = useCallback(
    (result: UploadResult<Record<string, unknown>>) => {
      const formData = new FormData();
      forEach(result.successful, (file) => {
        if (file.data instanceof Blob) {
          formData.append('files', file.data, file.name);
        }
      });

      uploadPaymentReceipts({
        orderId: order.id,
        body: formData,
      });
    },
    [order.id, uploadPaymentReceipts],
  );

  useEffect(() => {
    uppy.on('complete', handleFilesComplete);
    return () => {
      uppy.off('complete', handleFilesComplete);
    };
  }, [handleFilesComplete]);

  return (
    <DashboardModal
      open={open}
      uppy={uppy}
      plugins={['Webcam']}
      locale={Russian}
      proudlyDisplayPoweredByUppy={false}
      onRequestClose={onClose}
    />
  );
};
